import { validateEnumTranslations } from '../../safety_checks';

/**
 * État d'accès d'un professionnel (Volet A — gating d'accès).
 *
 * Valeur **dérivée** (jamais stockée telle quelle) de
 * `(trial_end_date, pro_subscription.status, subscription_end_date)` via
 * `computeAccessState()` (cf. access-utils.ts). Sert à piloter l'UX (bannière,
 * écran de gel) et l'enforcement backend (AccessFreezeGuard, gating envois).
 *
 * cf. docs/to_implement/trial-period-plan.md (§3).
 */
export enum AccessState {
  /** Essai en cours : `trial_end_date > now` et pas d'abo payant. */
  TRIALING = 'TRIALING',
  /** Essai dépassé sans souscription : blocage doux (lecture seule). */
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  /** Abonnement payant actif. */
  ACTIVE = 'ACTIVE',
  /** Impayé : relances épuisées (status Prisma `PAST_DUE`/`SUSPENDED`). */
  PAST_DUE = 'PAST_DUE',
  /** Abonnement annulé ou `subscription_end_date` atteint. */
  CANCELED = 'CANCELED',
}

/**
 * Niveau d'accès dérivé qui pilote UX + enforcement.
 *
 * `computeAccessLevel(access_state, payment_at_risk)` → un seul mécanisme pour
 * bannière, guard de gel et gating envois.
 */
export enum AccessLevel {
  /** TRIALING ou ACTIVE sain : tout marche (envois selon catégorie/quota). */
  FULL = 'FULL',
  /** ACTIVE + paiement en cours de relance : grâce (tout marche, bannière d'alerte). */
  WARNING = 'WARNING',
  /** TRIAL_EXPIRED / PAST_DUE / CANCELED : lecture seule, écrans bloquants. */
  FROZEN = 'FROZEN',
}

export const ACCESS_STATE_TRANSLATE_KEY = 'ACCESS_STATE_';
export const ACCESS_LEVEL_TRANSLATE_KEY = 'ACCESS_LEVEL_';

export const AccessStateTranslation = {
  'fr-FR': {
    [`${ACCESS_STATE_TRANSLATE_KEY}${AccessState.TRIALING}`]: 'Essai gratuit',
    [`${ACCESS_STATE_TRANSLATE_KEY}${AccessState.TRIAL_EXPIRED}`]: 'Essai terminé',
    [`${ACCESS_STATE_TRANSLATE_KEY}${AccessState.ACTIVE}`]: 'Abonnement actif',
    [`${ACCESS_STATE_TRANSLATE_KEY}${AccessState.PAST_DUE}`]: 'Paiement en échec',
    [`${ACCESS_STATE_TRANSLATE_KEY}${AccessState.CANCELED}`]: 'Abonnement terminé',
  },
};

export const AccessLevelTranslation = {
  'fr-FR': {
    [`${ACCESS_LEVEL_TRANSLATE_KEY}${AccessLevel.FULL}`]: 'Accès complet',
    [`${ACCESS_LEVEL_TRANSLATE_KEY}${AccessLevel.WARNING}`]: 'Action requise',
    [`${ACCESS_LEVEL_TRANSLATE_KEY}${AccessLevel.FROZEN}`]: 'Compte gelé',
  },
};

validateEnumTranslations(AccessState, AccessStateTranslation, ACCESS_STATE_TRANSLATE_KEY, true);
validateEnumTranslations(AccessLevel, AccessLevelTranslation, ACCESS_LEVEL_TRANSLATE_KEY, true);
